import type { CSSProperties, HTMLAttributes, Ref } from "react";

import { mark } from "~/components/LogoMark.css";

export interface LogoMarkProps extends HTMLAttributes<HTMLSpanElement> {
  /** Diameter in pixels. Defaults to 36 (prototype size). */
  size?: number;
  /**
   * Single letter shown inside the mark. Defaults to "M". Exposed
   * because the mark is also useful as a generic brand placeholder
   * in tests and storybook-style previews.
   */
  letter?: string;
  ref?: Ref<HTMLSpanElement>;
}

/**
 * Placeholder brand mark — olive-filled circle with a single letter.
 * Mirrors the prototype's sidebar emblem. Swap for a real wordmark
 * or emblem asset when design delivers one.
 */
export function LogoMark({
  size = 36,
  letter = "M",
  className,
  style,
  ref,
  ...rest
}: LogoMarkProps) {
  const merged =
    className !== undefined && className.length > 0
      ? `${mark} ${className}`
      : mark;
  const inlineStyle: CSSProperties = {
    width: size,
    height: size,
    // Font size tracks diameter so "M" always fills ~44% of the mark.
    fontSize: Math.round(size * 0.44),
    ...style,
  };
  return (
    <span
      ref={ref}
      className={merged}
      style={inlineStyle}
      aria-hidden="true"
      {...rest}
    >
      {letter}
    </span>
  );
}
